import { useState, useEffect } from "react";
import { useRecoilValue } from "recoil";
import { PropallyAgentV1Api, AgentMessageTemplate } from "../../api/generated/src";
import { AgentState } from "../../view_models/atoms";

const api = new PropallyAgentV1Api();

export type AgentMessageTemplateUpsert = {
  title: string;
  content: string;
  template_type: "investment" | "assessment";
  short_appeal_message?: string;
  order?: number | null;
};

export type AgentMessageTemplateReorderRequest = {
  template_id: number;
  target_id: number;
  position: "before" | "after";
};

export const useTemplates = () => {
  const agent = useRecoilValue(AgentState);
  const [templates, setTemplates] = useState<AgentMessageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const sortTemplates = (items: AgentMessageTemplate[]) => {
    return [...items].sort((a, b) => {
      const typeCompare = String(a.template_type).localeCompare(String(b.template_type));
      if (typeCompare !== 0) return typeCompare;
      const aOrder = a.order ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.order ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return (a.id ?? 0) - (b.id ?? 0);
    });
  };

  const setTemplatesLocal = (items: AgentMessageTemplate[]) => {
    setTemplates(sortTemplates(items));
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);

      const data = await new Promise<AgentMessageTemplate[]>((resolve, reject) => {
        api.agentV1MessageTemplatesGet({}, (error: object, data: AgentMessageTemplate[]) => {
          if (error) {
            reject(error);
          } else {
            resolve(data);
          }
        });
      });

      setTemplates(sortTemplates(data));
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const createTemplate = async (templateRequest: AgentMessageTemplateUpsert) => {
    try {
      const newTemplate = await new Promise<AgentMessageTemplate>((resolve, reject) => {
        api.agentV1MessageTemplatesPost(templateRequest, (error: object, data: AgentMessageTemplate) => {
          if (error) {
            reject(error);
          } else {
            resolve(data);
          }
        });
      });

      setTemplates((prev) => sortTemplates([...prev, newTemplate]));
      return newTemplate;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const updateTemplate = async (id: number, updates: AgentMessageTemplateUpsert) => {
    try {
      const updatedTemplate = await new Promise<AgentMessageTemplate>((resolve, reject) => {
        api.agentV1MessageTemplatesIdPatch(id, updates, (error: object, data: AgentMessageTemplate) => {
          if (error) {
            reject(error);
          } else {
            resolve(data);
          }
        });
      });

      setTemplates((prev) => sortTemplates(prev.map((t) => (t.id === id ? updatedTemplate : t))));
      return updatedTemplate;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const deleteTemplate = async (id: number) => {
    try {
      await new Promise<void>((resolve, reject) => {
        api.agentV1MessageTemplatesIdDelete(id, (error: object) => {
          if (error) {
            reject(error);
          } else {
            resolve();
          }
        });
      });

      setTemplates((prev) => sortTemplates(prev.filter((t) => t.id !== id)));
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const reorderTemplate = async (
    request: AgentMessageTemplateReorderRequest,
    options?: { skipLocalUpdate?: boolean }
  ) => {
    try {
      const data = await new Promise<AgentMessageTemplate[]>((resolve, reject) => {
        api.agentV1MessageTemplatesReorderPost(request, (error: object, data: AgentMessageTemplate[]) => {
          if (error) {
            reject(error);
          } else {
            resolve(data);
          }
        });
      });

      if (!options?.skipLocalUpdate && Array.isArray(data)) {
        setTemplates(sortTemplates(data));
      } else if (!options?.skipLocalUpdate) {
        await fetchTemplates();
      }
      return data;
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  return {
    templates,
    loading,
    error,
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    setTemplatesLocal,
    reorderTemplate,
  };
};
